import { useEffect, useRef, useState } from "react";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";
import { timer } from "helpers/functions";
import { accountStore, KYC_Step } from "entities/account";
import { CredentialsBox } from "../components/credentials-box";
import TermsCheckbox from "../components/terms-checkbox";
import Button from "shared/ui/button";
import { CountryModal } from "../components/country-modal";
import { GetCountryByCode } from "static/countries";
import { toast } from "react-toastify";
import { sircapApi } from "shared/api/sircap";
import { PATH_LIST } from "shared/lib/react-router";
import { useVerificationStep } from "hooks/useVerificationStep";
import { firebaseLogEvent } from "app/libs/firebase";
import {
  fbLogEventOnce,
  FB_EVENTS,
  requestAdvertiserTrackingAfterVerification,
} from "utils/fbEvents";
import { sessionStore } from "entities/session/model/sessionModel";
import Modal from "widgets/auth/modal";

// local type is unnecessary after moving state to store

export const CitizenshipDocumentsPage = () => {
  const { t } = useTranslation("app");
  const { t: o } = useTranslation("onboarding");
  const { t: err } = useTranslation("errors");
  const {
    kyc,
    residentInfoArray,
    setResidentInfoArray,
    modalOpened,
    setModalOpened,
    synckyc,
    only_armenia_resident,
    setOnlyArmeniaResident,
  } = useStore(accountStore);
  const [isChecked, setIsChecked] = useState(false);
  const [changeCountryModal, setChangeCountryModal] = useState(false);
  const [pendingCountryIndex, setPendingCountryIndex] = useState<number | null>(
    null
  );
  const [changeCountryDeleting, setChangeCountryDeleting] = useState(false);
  const [removeCountryModal, setRemoveCountryModal] = useState(false);
  const [removeCountryDeleting] = useState(false);

  // Validation function to check if all required documents are uploaded
  const validateAllDocuments = () => {
    if (!(kyc as any)?.documents) {
      // console.log("No KYC documents found");
      return false;
    }

    const result = residentInfoArray.every((item, index) => {
      const isMainCountry = index === 0;
      let docsToCheck;

      if (isMainCountry) {
        docsToCheck = (kyc as any).documents;
      } else {
        if (!(kyc as any).documents.OTHER_DOCUMENTS) return false;

        const countryDocs = (kyc as any).documents.OTHER_DOCUMENTS.find(
          (doc: any) => doc.COUNTRY === item.country
        );

        if (!countryDocs?.DOCS) return false;
        docsToCheck = countryDocs.DOCS;
      }

      // Check each required field for this country
      const hasIdCard =
        !!(docsToCheck.ID_CARD_FRONT_SIDE && docsToCheck.ID_CARD_BACK_SIDE) ||
        !!docsToCheck.PASSPORT;
      const hasInternalPassport = item.fields.internationalPassport
        ? !!docsToCheck.INTERNAL_PASSPORT
        : true;
      const hasSelfie = item.fields.selfie ? !!docsToCheck.SELFIE : true;
      const hasProofOfAddress = item.fields.proofOfAddress
        ? !!docsToCheck.UTILITY_BILL
        : true;
      // const hasPersonalAddress = item.fields.personalAddress
      //   ? kyc?.address_json !== null
      //   : true;

      // Check passport/ID card requirement
      const hasRequiredIdDocument = item.fields.passportOrIdCard
        ? hasIdCard
        : true;

      const isValid =
        hasRequiredIdDocument &&
        hasInternalPassport &&
        hasSelfie &&
        hasProofOfAddress;
      //  && hasPersonalAddress

      // console.log(
      //   `Country ${item.country} (${isMainCountry ? "main" : "additional"}):`,
      //   {
      //     hasRequiredIdDocument,
      //     hasInternalPassport,
      //     hasSelfie,
      //     hasProofOfAddress,
      //     hasPersonalAddress,
      //     isValid,
      //     fields: item.fields,
      //   }
      // );

      return isValid;
    });

    // console.log("Overall validation result:", result);
    return result;
  };

  const isAllDocumentsValid = validateAllDocuments();
  const docsUploadedTracked = useRef(false);

  // Check if radio selection is valid (only required for Armenia residents)
  const isRadioSelectionValid =
    residentInfoArray.length === 0 ||
    residentInfoArray[0]?.country !== "ARM" ||
    only_armenia_resident !== null;

  // *

  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAllDocumentsValid || docsUploadedTracked.current) return;
    docsUploadedTracked.current = true;
    const userId = sessionStore.getState().user?.id;
    void fbLogEventOnce(`kyc_docs_${userId}`, FB_EVENTS.kycDocumentsUploaded);
  }, [isAllDocumentsValid, residentInfoArray, kyc]);

  useEffect(() => {
    // const residence = kyc?.residence || "";
    if (!residentInfoArray || residentInfoArray.length === 0) {
      setResidentInfoArray([
        {
          country: "ARM",
          fields: {
            passportOrIdCard: true,
            internationalPassport: false,
            selfie: true,
            personalAddress: true,
            proofOfAddress: false,
          },
        },
      ]);
    }
  }, [residentInfoArray, setResidentInfoArray]);

  const closeModal = () => setModalOpened(null);

  const handleAdd = () => {
    setModalOpened(residentInfoArray.length);
  };

  const handleRemove = (index: number) => {
    const countryToRemove = residentInfoArray[index].country;

    const deleteDocument = async () => {
      try {
        const resp = await sircapApi.onboarding.deleteDocument(
          {},
          {
            country: countryToRemove,
          }
        );

        if (resp.error) {
          toast.error(err(String(resp.error.code)));
          return;
        }

        // Only remove from array if API call succeeds
        setResidentInfoArray((prev) => {
          const newArray = [...prev];
          newArray.splice(index, 1);
          return newArray;
        });
      } catch (error) {
        console.error("Error deleting document:", error);
        toast.error("Failed to delete document");
      }
    };

    deleteDocument();
    setChangeCountryModal(false);
    setRemoveCountryModal(false);
  };

  const requestRemove = (index: number) => {
    // If there are any uploaded docs for this index, show confirm modal instead
    const hasUploads = hasAnyUploadedDocsForIndex(index);
    if (hasUploads) {
      setPendingCountryIndex(index);
      setRemoveCountryModal(true);
    } else {
      handleRemove(index);
    }
  };

  const modalProps = {
    onClose: closeModal,
    onSelect: (country: string) => {
      const prevIndex = modalOpened!;
      const prevCountry = residentInfoArray[prevIndex]?.country;
      if (modalOpened === residentInfoArray.length) {
        // Adding a new item
        const newItem = {
          country: country,
          fields: {
            passportOrIdCard: true,
            internationalPassport: country === "RUS",
            selfie: false,
            personalAddress: false,
            proofOfAddress: false,
          },
        };
        setResidentInfoArray((prev) => [...prev, newItem]);

        // Scroll to bottom after adding new item
        timer(0.2).then(() => {
          if (scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTo({
              top: scrollableContainerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }
        });
      } else {
        // Updating existing item
        setResidentInfoArray((prev) => {
          const newArray = [...prev];
          newArray[modalOpened!].country = country;
          // Update fields based on new country
          newArray[modalOpened!].fields = {
            passportOrIdCard: true,
            internationalPassport: country === "RUS",
            selfie: modalOpened === 0,
            personalAddress: modalOpened === 0,
            proofOfAddress: modalOpened === 0 && country !== "ARM",
          };
          return newArray;
        });

        // After changing country, clear existing uploaded docs locally so UI becomes unmarked
        if (kyc) {
          const currentKyc: any = { ...(kyc as any) };
          const documentsRoot: any = { ...(currentKyc.documents || {}) };

          if (prevIndex === 0) {
            // Clearing main country docs
            currentKyc.documents = {};
          } else {
            const otherDocs: any[] = Array.isArray(
              documentsRoot.OTHER_DOCUMENTS
            )
              ? [...documentsRoot.OTHER_DOCUMENTS]
              : [];
            const filtered = otherDocs.filter(
              (doc: any) => doc?.COUNTRY !== prevCountry
            );
            currentKyc.documents = {
              ...documentsRoot,
              OTHER_DOCUMENTS: filtered,
            };
          }

          synckyc(currentKyc);
        }
      }
      closeModal();
    },
    label: t("residency"),
    selectedName:
      modalOpened === residentInfoArray.length
        ? undefined
        : GetCountryByCode(residentInfoArray[modalOpened!]?.country || "ARM")
            ?.name,
    diffGoBackIcon: true,
    usedCountries: residentInfoArray.map((item) => item.country),
  };

  // Checks if there are any uploaded documents/photos for the given country index
  const hasAnyUploadedDocsForIndex = (index: number) => {
    try {
      const isMainCountry = index === 0;
      const documentsRoot: any = (kyc as any)?.documents;
      if (!documentsRoot) return false;

      let docsToCheck: any = null;
      if (isMainCountry) {
        docsToCheck = documentsRoot;
      } else {
        const country = residentInfoArray[index]?.country;
        const otherDocs: any[] = documentsRoot.OTHER_DOCUMENTS || [];
        const countryDocs = otherDocs.find((d: any) => d?.COUNTRY === country);
        docsToCheck = countryDocs?.DOCS;
      }

      if (!docsToCheck) return false;

      const possibleKeys = [
        "ID_CARD_FRONT_SIDE",
        "ID_CARD_BACK_SIDE",
        "PASSPORT",
        "INTERNAL_PASSPORT",
        "SELFIE",
        "UTILITY_BILL",
      ];

      return possibleKeys.some((k) => !!docsToCheck[k]);
    } catch (e) {
      return false;
    }
  };

  const confirmChangeCountry = async () => {
    handleRemove(pendingCountryIndex!);
    if (pendingCountryIndex === null) return;
    try {
      const countryToClear = residentInfoArray[pendingCountryIndex]?.country;
      if (!countryToClear) {
        setChangeCountryModal(false);
        setPendingCountryIndex(null);
        return;
      }

      setChangeCountryDeleting(true);
      const resp = await sircapApi.onboarding.deleteDocument(
        {},
        { country: countryToClear }
      );
      if ((resp as any)?.error) {
        toast.error(err(String((resp as any).error.code)));
        setChangeCountryDeleting(false);
        return;
      }

      setChangeCountryModal(false);
      setChangeCountryDeleting(false);
      setModalOpened(pendingCountryIndex);
      setPendingCountryIndex(null);
    } catch (e) {
      setChangeCountryDeleting(false);
      toast.error("Failed to delete existing documents");
    }
  };

  // useEffect(() => {
  //   console.log(kyc);
  //   console.log("Validation result:", isAllDocumentsValid);
  //   console.log("Checkbox checked:", isChecked);
  // }, [kyc, isAllDocumentsValid, isChecked]);

  // useEffect(() => {
  //   console.log(residentInfoArray);
  //   console.log("====================================");
  //   console.log(kyc);
  //   console.log("====================================");
  // }, [residentInfoArray, kyc]);

  const { isLoading, onSubmit: submit } =
    useVerificationStep<KYC_Step.Documents>({
      step: KYC_Step.Documents,
      navigateOnSuccess: PATH_LIST.account,
      navigateParams: {
        state: { verificationRequestModal: true },
      },
      onSuccess: async () => {
        firebaseLogEvent("kyc_submitted");
        const userId = sessionStore.getState().user?.id;
        void fbLogEventOnce(`kyc_submit_${userId}`, FB_EVENTS.submitApplication);
        await requestAdvertiserTrackingAfterVerification();
      },
    });

  return (
    <div className="verification-upload-credentials">
      <div className="upload-credentials-main">
        <div className="upload-credentials-top">
          <p>{o("citizenshipDocuments")}</p>
          <span>{o("citizenshipDocumentsSubtitle")}</span>
        </div>

        <div
          className="upload-credentials-main-boxes"
          ref={scrollableContainerRef}
        >
          {residentInfoArray.map((item, index) => (
            <CredentialsBox
              disable={index === 0}
              item={{
                main: index === 0,
                country: item.country,
                fields: item.fields,
                // removable: true,
                removable: index !== 0,
              }}
              key={index}
              openModal={() => {
                const hasUploads = hasAnyUploadedDocsForIndex(index);
                if (hasUploads) {
                  setPendingCountryIndex(index);
                  setChangeCountryModal(true);
                } else {
                  setModalOpened(index);
                }
              }}
              country={item.country}
              remove={() => requestRemove(index)}
              showValidationErrors={false}
              isReadOnly={false}
              kyc={kyc}
              only_armenia_resident={only_armenia_resident}
              setOnlyArmeniaResident={setOnlyArmeniaResident}
              // removable={index === residentInfoArray.length - 1}
            />
          ))}
          <button className="upload-credentials-new-button" onClick={handleAdd}>
            <p>+</p>
            <p>{o("addCitizenship")}</p>
          </button>
        </div>
      </div>

      {modalOpened !== null && <CountryModal {...modalProps} />}

      <div className="upload-credentials-bottom-wrapper">
        <TermsCheckbox
          term={o("confirmAllCitizenshipCountries")}
          htmlForId="1"
          isChecked={isChecked}
          onChange={() => {
            setIsChecked(!isChecked);
          }}
        />
        <div className="upload-credentials-buttons">
          <Button
            type="submit"
            className="contract-sign-btn"
            variant="dark"
            onClick={() => submit({ only_armenia_resident })}
            disabled={
              !isChecked ||
              !isAllDocumentsValid ||
              !isRadioSelectionValid ||
              isLoading
            }
          >
            {t("confirm")}
          </Button>
        </div>
      </div>

      {(changeCountryModal || removeCountryModal) && (
        <Modal
          customModal="modal-log-out"
          onClick={
            changeCountryModal
              ? confirmChangeCountry
              : () => handleRemove(pendingCountryIndex!)
          }
          onHide={() => {
            setChangeCountryModal(false);
            setRemoveCountryModal(false);
          }}
          closeIcon={false}
          title={
            changeCountryModal ? o("changeCountry") : o("removeCitizenship")
          }
          text={
            changeCountryModal
              ? o("sureToChangeCountry")
              : o("sureToRemoveCitizenship")
          }
          btnText={changeCountryModal ? o("changeCountry") : t("remove")}
          isLoading={changeCountryDeleting || removeCountryDeleting}
          showSecondBtn={false}
          customOutlet="customOutlet"
        />
      )}
    </div>
  );
};
